import { useEffect, useState } from 'react';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import type { EntityMemberCapabilities } from '../types/entityMemberCapabilities';

export type EntitySummary = {
  id: string;
  name: string;
  type: 'person' | 'company' | 'networkclub' | 'mastermind' | 'community';
  role: 'owner' | 'organizer' | 'board' | 'va' | 'member';
  /** Present when role is organizer — granular powers (omit = volledige beheerder, backward compatible). */
  capabilities?: EntityMemberCapabilities | null;
  visibility?: 'public' | 'private';
  isPublic?: boolean;
  isActive?: boolean;
};

const STORAGE_KEY = 'activeEntityId';

export function useEntityContext(userId?: string | null) {
  const [entities, setEntities] = useState<EntitySummary[]>([]);
  const [activeEntityId, setActiveEntityId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) return;

    setLoading(true);
    setError(null);
    let cancelled = false;
    let entityUnsubs: Array<() => void> = [];

    const membersQuery = query(
      collection(db, 'entityMembers'),
      where('userId', '==', userId)
    );

    const clearEntityListeners = () => {
      entityUnsubs.forEach((unsub) => {
        try { unsub(); } catch {}
      });
      entityUnsubs = [];
    };

    const unsubscribe = onSnapshot(
      membersQuery,
      (membersSnap) => {
        try {
          clearEntityListeners();
          if (cancelled) return;

          const roleByEntityId = new Map<string, EntitySummary['role']>();
          const capsByEntityId = new Map<string, EntityMemberCapabilities | null | undefined>();
          membersSnap.docs.forEach((memberDoc) => {
            const member = memberDoc.data() as any;
            if (!member?.entityId) return;
            roleByEntityId.set(member.entityId, member.role as EntitySummary['role']);
            if (member.capabilities != null) {
              capsByEntityId.set(member.entityId, member.capabilities as EntityMemberCapabilities);
            }
          });

          const entityMap = new Map<string, EntitySummary>();
          const publish = () => {
            if (cancelled) return;
            const nextEntities = Array.from(entityMap.values());
            setEntities(nextEntities);

            const stored = localStorage.getItem(STORAGE_KEY);
            const hasStored = stored && nextEntities.some(entity => entity.id === stored);
            if (hasStored) {
              setActiveEntityId(stored as string);
            } else if (nextEntities.length > 0) {
              const preferredTypes = ['mastermind', 'community', 'networkclub'];
              const preferred =
                nextEntities.find(e => preferredTypes.includes(e.type) && e.isActive !== false) ??
                nextEntities.find(e => preferredTypes.includes(e.type)) ??
                nextEntities[0];
              setActiveEntityId(preferred.id);
              localStorage.setItem(STORAGE_KEY, preferred.id);
            } else {
              localStorage.removeItem(STORAGE_KEY);
              setActiveEntityId(null);
            }
            setLoading(false);
          };

          if (roleByEntityId.size === 0) {
            setEntities([]);
            localStorage.removeItem(STORAGE_KEY);
            setActiveEntityId(null);
            setLoading(false);
            return;
          }

          roleByEntityId.forEach((role, entityId) => {
            const unsubEntity = onSnapshot(
              doc(db, 'entities', entityId),
              (entitySnap) => {
                if (!entitySnap.exists()) {
                  entityMap.delete(entityId);
                  publish();
                  return;
                }

                const entity = entitySnap.data() as {
                  name: string;
                  type: EntitySummary['type'];
                  visibility?: 'public' | 'private';
                  isPublic?: boolean;
                  isActive?: boolean;
                };

                entityMap.set(entityId, {
                  id: entityId,
                  name: entity.name,
                  type: entity.type,
                  role,
                  capabilities: capsByEntityId.get(entityId),
                  visibility: entity.visibility,
                  isPublic: entity.isPublic,
                  isActive: entity.isActive,
                });
                publish();
              },
              (err) => {
                console.error('Error loading entity document:', err);
                entityMap.delete(entityId);
                publish();
              }
            );
            entityUnsubs.push(unsubEntity);
          });
        } catch (err) {
          if (cancelled) return;
          console.error('Error loading entity context:', err);
          setError('Kan entiteiten niet laden.');
          setLoading(false);
        }
      },
      (err) => {
        if (cancelled) return;
        console.error('Error loading entity context:', err);
        setError('Kan entiteiten niet laden.');
        setLoading(false);
      }
    );

    return () => {
      cancelled = true;
      clearEntityListeners();
      unsubscribe();
    };
  }, [userId]);

  // Sync active entity immediately when other UI parts (e.g. navbar Groups menu)
  // switch entities in the same tab.
  useEffect(() => {
    const handleActiveEntityChanged = (event: Event) => {
      const customEvent = event as CustomEvent<{ entityId?: string }>;
      const entityId = customEvent.detail?.entityId;
      if (!entityId) return;
      setActiveEntityId(entityId);
      localStorage.setItem(STORAGE_KEY, entityId);
    };

    window.addEventListener('activeEntityChanged', handleActiveEntityChanged as EventListener);
    return () => {
      window.removeEventListener('activeEntityChanged', handleActiveEntityChanged as EventListener);
    };
  }, []);

  const setActiveEntity = (entityId: string) => {
    setActiveEntityId(entityId);
    localStorage.setItem(STORAGE_KEY, entityId);
    window.dispatchEvent(new CustomEvent('activeEntityChanged', { detail: { entityId } }));
  };

  return {
    entities,
    activeEntityId,
    setActiveEntityId: setActiveEntity,
    loading,
    error
  };
}
